import React, { useEffect, useRef, useState } from "react";
import "./Highlights.css";

// ==========================================
// TEDxSETAPAK 2025 YOUTUBE VIDEOS
// ==========================================

const videos = [
  { name: "Eva Ng", youtubeId: "aUkq4qEh7t4" },
  { name: "Mannisa Lee", youtubeId: "r7CbfAZL7B0" },
  { name: "Jin Yang", youtubeId: "FB1c2XMLHQE" },
  { name: "Kim Low", youtubeId: "wo-yQbTCxSg" },
  { name: "Clement Ong", youtubeId: "bnVCKkEPmz0" },
  { name: "Dr Saras", youtubeId: "Y4q0FWvzPGw" },
  { name: "Racheel Ang", youtubeId: "_h_VA90upS8" },
  { name: "Gan Zhen Nee", youtubeId: "Ir02US8lLF0" },
  { name: "Ang Wee Kien", youtubeId: "QLlWln7bX2o" },
];

// ==========================================
// REUSABLE HORIZONTAL PHOTO GALLERY
// ==========================================

function PhotoGallery({
  label,
  title,
  description,
  photos,
  onPhotoClick,
}) {
  const scrollRef = useRef(null);

  const scrollGallery = (direction) => {
    if (!scrollRef.current) return;

    const scrollAmount = scrollRef.current.clientWidth * 0.75;

    scrollRef.current.scrollBy({
      left: direction * scrollAmount,
      behavior: "smooth",
    });
  };

  if (!photos?.length) return null;

  return (
    <div className="photo-gallery-section">

      {/* Gallery Header */}
      <div className="photo-gallery-header">

        <div className="photo-gallery-heading">

          <span className="photo-gallery-label">
            {label}
          </span>

          <h2 className="photo-gallery-title">
            {title}
          </h2>

          <p className="photo-gallery-description">
            {description}
          </p>

        </div>

        {/* Navigation */}
        <div className="photo-gallery-navigation">

          <button
            type="button"
            onClick={() => scrollGallery(-1)}
            aria-label={`Scroll ${label} photos left`}
          >
            <i className="fas fa-arrow-left"></i>
          </button>

          <button
            type="button"
            onClick={() => scrollGallery(1)}
            aria-label={`Scroll ${label} photos right`}
          >
            <i className="fas fa-arrow-right"></i>
          </button>

        </div>

      </div>

      {/* Horizontal Gallery */}
      <div
        className="photo-gallery-track"
        ref={scrollRef}
      >

        {photos.map((src, index) => (
          <button
            key={`${src}-${index}`}
            type="button"
            className="photo-gallery-card"
            onClick={() => onPhotoClick(index)}
            aria-label={`View ${label} photo ${index + 1}`}
          >
            <img
              src={src}
              alt={`${label} ${index + 1}`}
              loading="lazy"
            />

            <div className="photo-gallery-overlay">
              <i className="fas fa-expand"></i>
            </div>
          </button>
        ))}

      </div>

      {/* Gallery Footer */}
      <div className="photo-gallery-footer">

        <span>
          {photos.length} MOMENTS
        </span>

        <span>
          SCROLL TO EXPLORE
          <i className="fas fa-arrow-right ms-2"></i>
        </span>

      </div>

    </div>
  );
}

// ==========================================
// MAIN HIGHLIGHTS COMPONENT
// ==========================================

export default function Highlights() {
  const [data, setData] = useState(null);

  // Shared fullscreen viewer state
  const [viewer, setViewer] = useState(null);

  // ==========================================
  // LOAD PHOTO DATA
  // ==========================================

  useEffect(() => {
    fetch("/data/highlights.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load highlights.json");
        }

        return response.json();
      })
      .then(setData)
      .catch((error) => {
        console.error("Failed to load highlights:", error);

        setData({
          highlights: [],
          bts: [],
        });
      });
  }, []);

  // ==========================================
  // FULLSCREEN VIEWER FUNCTIONS
  // ==========================================

  const openViewer = (gallery, index) => {
    setViewer({
      gallery,
      index,
    });
  };

  const closeViewer = () => {
    setViewer(null);
  };

  const changePhoto = (direction) => {
    setViewer((current) => {
      if (!current || !data) return current;

      const photos = data[current.gallery] || [];

      if (!photos.length) return current;

      return {
        ...current,
        index:
          (current.index + direction + photos.length) %
          photos.length,
      };
    });
  };

  // ==========================================
  // KEYBOARD SUPPORT
  // ==========================================

  useEffect(() => {
    if (!viewer) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setViewer(null);
      }

      if (event.key === "ArrowLeft") {
        changePhoto(-1);
      }

      if (event.key === "ArrowRight") {
        changePhoto(1);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Prevent background scrolling
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [viewer, data]);

  // ==========================================
  // LOADING
  // ==========================================

  if (!data) {
    return (
      <section
        id="highlights"
        className="highlights-section text-white bg-black py-5"
      >
        <div className="container text-center">
          Loading highlights…
        </div>
      </section>
    );
  }

  // Current fullscreen gallery
  const activePhotos = viewer
    ? data[viewer.gallery] || []
    : [];

  const activePhoto =
    viewer && activePhotos.length
      ? activePhotos[viewer.index]
      : null;

  // ==========================================
  // MAIN PAGE
  // ==========================================

  return (
    <section
      id="highlights"
      className="highlights-section text-white bg-black py-5"
    >

      <div className="container">

        {/* ==========================================
            MAIN HEADING
            ========================================== */}

        <h2 className="highlights-main-title text-center">
          Event Highlights
        </h2>

        {/* ==========================================
            YOUTUBE VIDEO GALLERY
            ========================================== */}

        <div className="highlights-videos">

          <h3 className="mb-3 text-center">
            TEDxSetapak 2025 Talks
          </h3>

          <p className="text-center highlights-subtitle mb-5">
            Ideas worth sharing. Stories worth hearing.
          </p>

          <div className="row g-4">

            {videos.map((video) => {
              const thumbnail =
                `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`;

              const youtubeUrl =
                `https://www.youtube.com/watch?v=${video.youtubeId}`;

              return (
                <div
                  key={video.youtubeId}
                  className="col-12 col-md-6 col-lg-4"
                >

                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="highlight-video-card"
                    aria-label={`Watch ${video.name}'s TEDxSetapak talk`}
                  >

                    <div className="highlight-video-thumbnail">

                      <img
                        src={thumbnail}
                        alt={`${video.name} TEDxSetapak talk`}
                        loading="lazy"
                      />

                      <div className="highlight-play-button">
                        <i
                          className="fas fa-play"
                          aria-hidden="true"
                        ></i>
                      </div>

                    </div>

                    <div className="highlight-video-info">

                      <h4>{video.name}</h4>

                      <span>
                        Watch Talk
                        <i className="fas fa-arrow-right"></i>
                      </span>

                    </div>

                  </a>

                </div>
              );
            })}

          </div>

        </div>

        {/* ==========================================
            EVENT PHOTOS - HORIZONTAL GALLERY
            ========================================== */}

        <PhotoGallery
          label="EVENT HIGHLIGHTS"
          title="Moments Worth Remembering"
          description="Relive the inspiring moments, powerful conversations, and unforgettable experiences of TEDxSetapak 2025."
          photos={data.highlights}
          onPhotoClick={(index) =>
            openViewer("highlights", index)
          }
        />

        {/* ==========================================
            BEHIND THE SCENES - HORIZONTAL GALLERY
            ========================================== */}

        <PhotoGallery
          label="BEHIND THE SCENES"
          title="The People Behind the Ideas"
          description="A glimpse of the moments, teamwork, and memories that made TEDxSetapak 2025 possible."
          photos={data.bts}
          onPhotoClick={(index) =>
            openViewer("bts", index)
          }
        />

      </div>

      {/* ==========================================
          SHARED FULLSCREEN PHOTO VIEWER
          ========================================== */}

      {activePhoto && (
        <div
          className="photo-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          onClick={closeViewer}
        >

          {/* Close */}
          <button
            type="button"
            className="photo-lightbox-close"
            onClick={closeViewer}
            aria-label="Close photo viewer"
          >
            <i className="fas fa-times"></i>
          </button>

          {/* Previous */}
          <button
            type="button"
            className="photo-lightbox-prev"
            onClick={(event) => {
              event.stopPropagation();
              changePhoto(-1);
            }}
            aria-label="Previous photo"
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          {/* Fullscreen Photo */}
          <img
            src={activePhoto}
            alt={`Photo ${viewer.index + 1}`}
            onClick={(event) => event.stopPropagation()}
          />

          {/* Next */}
          <button
            type="button"
            className="photo-lightbox-next"
            onClick={(event) => {
              event.stopPropagation();
              changePhoto(1);
            }}
            aria-label="Next photo"
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          {/* Counter */}
          <div className="photo-lightbox-counter">
            {viewer.index + 1} / {activePhotos.length}
          </div>

        </div>
      )}

    </section>
  );
}